'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { StorefrontPopup } from '@eliadmin/core/src/api/popups/popup-types';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React from 'react';
import { LuAirplay } from 'react-icons/lu';
import { create } from 'zustand';

const POPUP_SUPPRESS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const LEGACY_SUPPRESS_STORAGE_KEY = 'popup_suppress_v1';

type CssTarget = {
  className?: string;
  style?: React.CSSProperties;
};

type PopupCss = {
  trigger?: CssTarget;
  content?: CssTarget;
  headline?: CssTarget;
  imageWrapper?: CssTarget;
  image?: CssTarget;
  body?: CssTarget;
  button?: CssTarget;
};

type PopupQueueState = {
  contextualPopups: StorefrontPopup[];
  contextualSource: string | null;
  seenPopupKeys: string[];
  registerContextualPopups: (source: string, popups: StorefrontPopup[]) => void;
  unregisterContextualPopups: (source: string) => void;
  markPopupSeen: (key: string) => void;
};

const usePopupQueueStore = create<PopupQueueState>(set => ({
  contextualPopups: [],
  contextualSource: null,
  seenPopupKeys: [],
  registerContextualPopups: (source, popups) =>
    set({
      contextualSource: source,
      contextualPopups: popups,
    }),
  unregisterContextualPopups: source =>
    set(state =>
      state.contextualSource === source ? { contextualSource: null, contextualPopups: [] } : state,
    ),
  markPopupSeen: key =>
    set(state =>
      state.seenPopupKeys.includes(key) ? state : { seenPopupKeys: [...state.seenPopupKeys, key] },
    ),
}));

function parsePopupCss(popupCssString?: string | null): PopupCss | undefined {
  if (!popupCssString) return undefined;

  try {
    return (JSON.parse(popupCssString) as PopupCss) || undefined;
  } catch {
    return undefined;
  }
}

function popupSignature(popup: StorefrontPopup): string {
  return JSON.stringify({
    headline: popup.headline ?? '',
    content: popup.content ?? '',
    image: popup.image?.url ?? '',
    redirectUrl: popup.popup_redirect_url ?? '',
    buttonText: popup.popup_button_text ?? '',
  });
}

function legacyPopupSignature(popup: StorefrontPopup): string {
  return JSON.stringify({
    h: popup.headline ?? '',
    c: popup.content ?? '',
    i: popup.image?.url ?? '',
  });
}

function hashString(value: string): string {
  let hash = 5381;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 33) ^ value.charCodeAt(index);
  }

  return (hash >>> 0).toString(36);
}

function popupIdentity(popup: StorefrontPopup): string {
  return popup.id ? `id-${popup.id}` : `legacy-${hashString(popupSignature(popup))}`;
}

function popupSeenKey(popup: StorefrontPopup): string {
  return `${popupIdentity(popup)}:${hashString(popupSignature(popup))}`;
}

function popupSuppressStorageKey(popup: StorefrontPopup): string {
  return `eliadmin:popup:suppress:${popupIdentity(popup)}`;
}

function isPopupSuppressed(popup: StorefrontPopup): boolean {
  try {
    const raw = localStorage.getItem(popupSuppressStorageKey(popup));
    if (raw) {
      const parsed = JSON.parse(raw) as { signature?: string; until?: number };
      if (
        parsed.signature === popupSignature(popup) &&
        typeof parsed.until === 'number' &&
        parsed.until > Date.now()
      ) {
        return true;
      }
    }

    const legacyRaw = localStorage.getItem(LEGACY_SUPPRESS_STORAGE_KEY);
    if (!legacyRaw) return false;

    const legacy = JSON.parse(legacyRaw) as { signature?: string; until?: number };
    return (
      legacy.signature === legacyPopupSignature(popup) &&
      typeof legacy.until === 'number' &&
      legacy.until > Date.now()
    );
  } catch {
    return false;
  }
}

function setPopupSuppressed(popup: StorefrontPopup, suppressed: boolean): void {
  try {
    const storageKey = popupSuppressStorageKey(popup);

    if (!suppressed) {
      localStorage.removeItem(storageKey);
      return;
    }

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        signature: popupSignature(popup),
        until: Date.now() + POPUP_SUPPRESS_TTL_MS,
      }),
    );
  } catch {
    // localStorage may be unavailable or full; popup rendering must continue.
  }
}

function uniqueActivePopups(popups: Array<StorefrontPopup | null | undefined>): StorefrontPopup[] {
  const seen = new Set<string>();

  return popups.filter((popup): popup is StorefrontPopup => {
    if (!popup?.is_active) return false;

    const key = popupSeenKey(popup);
    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
}

export function ContextualPopups({ popups }: { popups?: StorefrontPopup[] | null }) {
  const source = React.useId();
  const registerContextualPopups = usePopupQueueStore(state => state.registerContextualPopups);
  const unregisterContextualPopups = usePopupQueueStore(state => state.unregisterContextualPopups);
  const normalizedPopups = React.useMemo(() => (Array.isArray(popups) ? popups : []), [popups]);

  React.useEffect(() => {
    registerContextualPopups(source, normalizedPopups);

    return () => unregisterContextualPopups(source);
  }, [normalizedPopups, registerContextualPopups, source, unregisterContextualPopups]);

  return null;
}

export function Popup() {
  const globalPopup = useGlobalStore(state => state.generalLayout?.popup);
  const contextualPopups = usePopupQueueStore(state => state.contextualPopups);
  const seenPopupKeys = usePopupQueueStore(state => state.seenPopupKeys);
  const markPopupSeen = usePopupQueueStore(state => state.markPopupSeen);
  const { t } = useTranslations();

  const popups = React.useMemo(
    () => uniqueActivePopups([...contextualPopups, globalPopup]),
    [contextualPopups, globalPopup],
  );
  const queueKey = popups.map(popupSeenKey).join('|');
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const [dontShowNext, setDontShowNext] = React.useState(false);
  const currentPopup = popups[currentIndex];
  const currentPopupKey = currentPopup ? popupSeenKey(currentPopup) : null;
  const css = React.useMemo(
    () => parsePopupCss(currentPopup?.popup_css),
    [currentPopup?.popup_css],
  );

  React.useEffect(() => {
    setCurrentIndex(0);
    setOpen(false);
  }, [queueKey]);

  React.useEffect(() => {
    if (!currentPopup || !currentPopupKey) return;

    setOpen(false);
    setDontShowNext(false);

    if (seenPopupKeys.includes(currentPopupKey) || isPopupSuppressed(currentPopup)) {
      const skipHandle = window.setTimeout(() => setCurrentIndex(index => index + 1), 0);
      return () => window.clearTimeout(skipHandle);
    }

    const seconds = Number(currentPopup.time ?? 0);
    if (!Number.isFinite(seconds) || seconds < 0) return;

    const openHandle = window.setTimeout(() => setOpen(true), seconds * 1000);
    return () => window.clearTimeout(openHandle);
  }, [currentPopup, currentPopupKey, queueKey, seenPopupKeys]);

  if (!currentPopup || !currentPopupKey) return null;

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen && open) {
      markPopupSeen(currentPopupKey);
    }
  };

  const hasImage = !!currentPopup.image?.url;
  const suppressCheckboxId = `popup-suppress-${popupIdentity(currentPopup)}`;

  return (
    <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <ResponsiveDialogTrigger asChild>
        <Button
          variant="default"
          size="icon"
          className={cn(
            'fixed bottom-16 right-4 z-[1000] h-12 w-12 rounded-full shadow-xl transition-transform hover:scale-105 sm:bottom-8',
            'ea-popup__trigger',
            css?.trigger?.className,
          )}
          style={css?.trigger?.style}
          aria-label={currentPopup.headline || t('text3527')}
        >
          <LuAirplay size={22} aria-hidden="true" />
        </Button>
      </ResponsiveDialogTrigger>

      <ResponsiveDialogContent
        className={cn(
          'gap-0 overflow-hidden border-0 bg-background p-0 shadow-2xl sm:max-w-xl',
          'ea-popup__content',
          css?.content?.className,
        )}
        dialogProps={{ style: css?.content?.style }}
        drawerProps={{ style: css?.content?.style }}
      >
        <ResponsiveDialogTitle hidden>
          {currentPopup.headline || t('text3527')}
        </ResponsiveDialogTitle>

        {hasImage ? (
          <div
            className={cn(
              'relative flex max-h-72 min-h-40 w-full items-center justify-center overflow-hidden bg-muted',
              'ea-popup__image-wrapper',
              css?.imageWrapper?.className,
            )}
            style={css?.imageWrapper?.style}
          >
            <Image
              src={currentPopup.image!.url}
              alt={resolveImageAlt(currentPopup?.image, 'popup-image')}
              width={720}
              height={360}
              className={cn(
                'max-h-72 w-full object-cover',
                'ea-popup__image',
                css?.image?.className,
              )}
              style={css?.image?.style}
            />
          </div>
        ) : (
          <div className="ea-popup__accent h-1.5 w-full bg-primary" />
        )}

        <div className="ea-popup__body-wrapper flex flex-col gap-5 p-6 sm:p-8">
          {popups.length > 1 && (
            <span className="ea-popup__counter w-fit rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
              {currentIndex + 1} / {popups.length}
            </span>
          )}

          {currentPopup.headline && (
            <h2
              className={cn(
                'pr-6 text-2xl font-semibold leading-tight sm:text-3xl',
                'ea-popup__headline',
                css?.headline?.className,
              )}
              style={css?.headline?.style}
            >
              {currentPopup.headline}
            </h2>
          )}

          {currentPopup.content && (
            <div
              className={cn(
                'text-sm leading-relaxed text-muted-foreground sm:text-base',
                'ea-popup__body',
                css?.body?.className,
              )}
              style={css?.body?.style}
            >
              <HtmlRenderer html={currentPopup.content} />
            </div>
          )}

          <label
            htmlFor={suppressCheckboxId}
            className="ea-popup__suppress flex cursor-pointer items-center gap-2.5 text-sm text-muted-foreground"
          >
            <Checkbox
              id={suppressCheckboxId}
              checked={dontShowNext}
              onCheckedChange={value => {
                const checked = Boolean(value);
                setDontShowNext(checked);
                setPopupSuppressed(currentPopup, checked);
              }}
            />
            <span>{t('text2511')}</span>
          </label>

          {currentPopup.popup_redirect_url && (
            <div className="ea-popup__actions flex justify-end">
              <ResponsiveDialogClose asChild>
                <Button
                  asChild
                  className={cn('w-full sm:w-auto', 'ea-popup__button', css?.button?.className)}
                  style={css?.button?.style}
                >
                  <Link href={currentPopup.popup_redirect_url}>
                    {currentPopup.popup_button_text || 'Open'}
                  </Link>
                </Button>
              </ResponsiveDialogClose>
            </div>
          )}
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
